/**
 * Memo viewer for memo / DOCX runs (Proposal Builder, Redesign in
 * Eraneos, any agent whose final step is docx-render). Mirror of
 * the deck viewer — same comment-thread + regenerate flow, scoped
 * to memo_sections rows.
 *
 *   #runs/<runId>/memo  →  this component
 *
 * Per-section regeneration is best-effort and synchronous: pulls
 * the section's stored markdown, asks Claude to rewrite it from
 * the open comments + optional notes, swaps the stored markdown,
 * rebuilds the WHOLE .docx, returns the new download URL.
 */
import React from 'react';
import { useSubCrumbs } from './route-title-context.jsx';
import { publicOrigin } from '../lib/public-origin.ts';
import { exportShellUrl } from '../lib/export-shell.ts';

// URL helpers — agent-keyed or chat-keyed surface based on mode.
// Both surfaces share JSON shape (GET → { run, sections }; POST
// → ok / downloadUrl).
function memoBase(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/memo`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/memo`;
}
function previewUrl(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/memo/preview`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/memo/preview`;
}
function exportPdfUrl(mode, id) {
  return mode === 'chat'
    ? `/api/chat-artefacts/${encodeURIComponent(id)}/memo/export/pdf`
    : `/api/agents/_/runs/${encodeURIComponent(id)}/memo/export/pdf`;
}

async function memoGet(mode, id) {
  const res = await fetch(memoBase(mode, id), { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `memo → ${res.status}`);
  return j;
}

async function memoPost(mode, id, payload) {
  const res = await fetch(memoBase(mode, id), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `memo POST → ${res.status}`);
  return j;
}

function fmt(t) {
  if (!t) return '—';
  return new Date(t).toLocaleString();
}

/**
 * Minimal markdown renderer for the preview pane. Same scope as the
 * server-side html-preview helper: headings, ul/ol, bold/italic,
 * paragraphs. Inline because rendering inside a React tree means
 * we want safe nodes rather than dangerouslySetInnerHTML.
 */
function renderSectionMarkdown(md) {
  const lines = (md || '').split(/\r?\n/);
  const out = [];
  let i = 0;
  let listKind = null;
  let listItems = [];
  const flushList = (key) => {
    if (listItems.length > 0) {
      const items = listItems.slice();
      out.push({ kind: listKind, key, items });
      listItems = [];
    }
    listKind = null;
  };
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) { flushList(`l${i}`); i++; continue; }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushList(`l${i}`);
      out.push({ kind: 'h', level: Math.min(6, heading[1].length), text: heading[2], key: `h${i}` });
      i++; continue;
    }
    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      if (listKind !== 'ul') flushList(`l${i}`);
      listKind = 'ul';
      listItems.push(ul[1]);
      i++; continue;
    }
    const ol = /^\d+\.\s+(.*)$/.exec(line);
    if (ol) {
      if (listKind !== 'ol') flushList(`l${i}`);
      listKind = 'ol';
      listItems.push(ol[1]);
      i++; continue;
    }
    flushList(`l${i}`);
    const para = [line];
    i++;
    while (i < lines.length) {
      const next = lines[i].trim();
      if (!next || /^(#{1,6})\s/.test(next) || /^[-*]\s/.test(next) || /^\d+\.\s/.test(next)) break;
      para.push(next);
      i++;
    }
    out.push({ kind: 'p', text: para.join(' '), key: `p${i}` });
  }
  flushList('lend');
  return out;
}

function renderInline(text) {
  // Returns an array of React nodes interpreting **bold**, *italic*,
  // `code`. Keeps things plain otherwise — links, images, footnotes
  // aren't in scope (the source markdown is generated by drafters,
  // not free-form input).
  const parts = [];
  let rest = text;
  let keyCounter = 0;
  // Strategy: scan for the next mark; emit plain text + marked span.
  // Simpler than a real parser, fine for the shapes we see.
  const re = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(rest)) !== null) {
    if (m.index > last) parts.push(rest.slice(last, m.index));
    const tok = m[1];
    keyCounter++;
    if (tok.startsWith('**')) {
      parts.push(<strong key={keyCounter}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*')) {
      parts.push(<em key={keyCounter}>{tok.slice(1, -1)}</em>);
    } else {
      parts.push(<code key={keyCounter} style={{fontFamily:'SF Mono, Menlo, monospace',fontSize:'0.9em',background:'var(--bg-alt)',padding:'0 4px',borderRadius:3}}>{tok.slice(1, -1)}</code>);
    }
    last = m.index + tok.length;
  }
  if (last < rest.length) parts.push(rest.slice(last));
  return parts;
}

function SectionCard({ section, onComment, onRegenerate, onApprove, onUnapprove, busy }) {
  const isApproved = !!section.approvedAt;
  const [commentDraft, setCommentDraft] = React.useState('');
  const [regenNotes, setRegenNotes] = React.useState('');
  const [showRegen, setShowRegen] = React.useState(false);
  const openComments = (section.comments || []).filter(c => !c.resolvedAt);
  const blocks = renderSectionMarkdown(section.markdown || '');

  const submitComment = (e) => {
    e.preventDefault();
    const body = commentDraft.trim();
    if (!body) return;
    onComment(section.index, body);
    setCommentDraft('');
  };

  const submitRegen = () => {
    onRegenerate(section.index, regenNotes.trim());
    setRegenNotes('');
    setShowRegen(false);
  };

  return (
    <div style={{
      border:'1px solid var(--border)',
      borderRadius:'var(--r-lg)',
      background:'var(--bg)',
      marginBottom:'var(--space-4)',
      overflow:'hidden',
    }}>
      <div style={{
        display:'flex',alignItems:'center',gap:'var(--space-3)',
        padding:'var(--space-3) var(--space-5)',
        background:'var(--bg-alt)',
        borderBottom:'1px solid var(--border)',
      }}>
        <div style={{fontFamily:'var(--font-display)',fontSize:'var(--text-lg)'}}>
          Section {section.index} — {section.title || '(untitled)'}
        </div>
        {section.status === 'regenerating' && (
          <span className="pill pill--running">regenerating…</span>
        )}
        {section.status === 'failed' && (
          <span className="pill" style={{background:'#FCE6E0',color:'#A20235'}} title={section.error || ''}>
            failed
          </span>
        )}
        <div style={{flex:1}}/>
        <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
          {fmt(section.updatedAt)}
        </span>
      </div>

      <div className="respo-sidebar">
        {/* Section preview */}
        <div className="block-pane block-pane--content" style={{padding:'var(--space-5)'}}>
          {blocks.map(b => {
            if (b.kind === 'h') {
              const lvl = Math.max(2, Math.min(4, b.level + 1));  // bump down so the section heading sits below the card title
              const Tag = `h${lvl}`;
              return <Tag key={b.key} style={{margin:'var(--space-3) 0 var(--space-2)'}}>{renderInline(b.text)}</Tag>;
            }
            if (b.kind === 'p') return <p key={b.key} style={{margin:'var(--space-2) 0',lineHeight:'var(--lh-normal)'}}>{renderInline(b.text)}</p>;
            if (b.kind === 'ul') return (
              <ul key={b.key} style={{margin:'var(--space-2) 0',paddingLeft:'var(--space-5)'}}>
                {b.items.map((it, j) => <li key={j} style={{marginBottom:'var(--space-1)'}}>{renderInline(it)}</li>)}
              </ul>
            );
            if (b.kind === 'ol') return (
              <ol key={b.key} style={{margin:'var(--space-2) 0',paddingLeft:'var(--space-5)'}}>
                {b.items.map((it, j) => <li key={j} style={{marginBottom:'var(--space-1)'}}>{renderInline(it)}</li>)}
              </ol>
            );
            return null;
          })}
        </div>

        {/* Comments rail */}
        <div style={{padding:'var(--space-4)',background:'var(--bg-alt)',display:'flex',flexDirection:'column',gap:'var(--space-3)'}}>
          <div style={{fontSize:'var(--text-xs)',letterSpacing:'0.04em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>
            Comments {openComments.length > 0 ? `(${openComments.length} open)` : ''}
          </div>
          {(section.comments || []).length === 0 && (
            <div style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)'}}>No comments yet. Add one to refine this section.</div>
          )}
          {(section.comments || []).map(c => (
            <div key={c.id} style={{
              padding:'var(--space-2) var(--space-3)',
              background:'var(--bg)',
              border:'1px solid var(--border)',
              borderRadius:'var(--r-sm)',
              fontSize:'var(--text-sm)',
              opacity: c.resolvedAt ? 0.6 : 1,
              textDecoration: c.resolvedAt ? 'line-through' : 'none',
            }}>
              <div>{c.body}</div>
              <div style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginTop:'var(--space-1)'}}>
                {fmt(c.createdAt)} · {c.resolvedAt ? 'resolved' : 'open'}
              </div>
            </div>
          ))}
          <form onSubmit={submitComment} style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
            <textarea
              className="textarea"
              value={commentDraft}
              onChange={e => setCommentDraft(e.target.value)}
              placeholder="What should change in this section?"
              rows={2}
              style={{fontFamily:'inherit'}}
            />
            <button type="submit" className="btn-ghost" disabled={busy || !commentDraft.trim()}>
              Add comment
            </button>
          </form>
          <div style={{borderTop:'1px solid var(--border)',paddingTop:'var(--space-3)',display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
            <button
              type="button"
              className={isApproved ? 'btn-orange' : 'btn-ghost'}
              onClick={() => isApproved ? onUnapprove(section.index) : onApprove(section.index)}
              disabled={busy || section.status === 'regenerating'}
              style={{
                width:'100%',
                background: isApproved ? '#00702A' : undefined,
                borderColor: isApproved ? '#00702A' : undefined,
                color: isApproved ? '#fff' : undefined,
              }}
              title={isApproved ? 'Click to mark this section as needing changes' : 'Mark this section as approved — export buttons enable once every section is approved'}
            >
              {isApproved ? '✓ Approved — click to unapprove' : 'Approve this section'}
            </button>
            {!showRegen && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setShowRegen(true)}
                disabled={busy || section.status === 'regenerating'}
                style={{width:'100%'}}
              >
                {openComments.length > 0 ? `Regenerate from ${openComments.length} comment${openComments.length === 1 ? '' : 's'}` : 'Regenerate section…'}
              </button>
            )}
            {showRegen && (
              <div style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
                <textarea
                  className="textarea"
                  value={regenNotes}
                  onChange={e => setRegenNotes(e.target.value)}
                  placeholder="Extra notes (optional). Open comments above are already applied."
                  rows={2}
                  style={{fontFamily:'inherit'}}
                />
                <div style={{display:'flex',gap:'var(--space-2)'}}>
                  <button type="button" className="btn-orange" onClick={submitRegen} disabled={busy || (openComments.length === 0 && !regenNotes.trim())} style={{flex:1}}>
                    Regenerate
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => setShowRegen(false)} disabled={busy}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MemoView({ subPath, mode = 'agent' }) {
  // subPath shape: "<runId>/memo" (optionally "…?agent=<id>")
  const runId = (subPath || '').split('/')[0] || '';
  // Explicit owning agent from the ?agent=<id> query string (spawn-*
  // skills append it). null when absent — drives the breadcrumb's
  // parent link, so we only deep-link to a specific agent's run when we
  // actually know which agent owns it.
  const explicitAgentId = React.useMemo(() => {
    if (mode === 'chat') return null;
    const queryIdx = (subPath || '').indexOf('?');
    if (queryIdx === -1) return null;
    const params = new URLSearchParams((subPath || '').slice(queryIdx + 1));
    return params.get('agent') || null;
  }, [subPath, mode]);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [lastDownloadUrl, setLastDownloadUrl] = React.useState(null);
  // Progress for the bulk "Regenerate all pending" button. Lives at
  // the top-level so hooks order stays stable across the early returns;
  // the filter + handler below run only after data has loaded.
  const [bulkProgress, setBulkProgress] = React.useState(null);
  const [shareUrl, setShareUrl] = React.useState(null);
  const [shareBusy, setShareBusy] = React.useState(false);
  const [shareError, setShareError] = React.useState(null);
  const [shareCopied, setShareCopied] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!runId) return;
    setError(null);
    try {
      const res = await memoGet(mode, runId);
      setData(res);
    } catch (err) {
      setError(err.message || String(err));
    }
  }, [runId]);

  React.useEffect(() => { reload(); }, [reload]);

  React.useEffect(() => {
    const regenerating = (data?.sections || []).some(s => s.status === 'regenerating');
    if (!regenerating) return undefined;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [data?.sections, reload]);

  // Breadcrumb: show the run's title (not the raw run-id UUID) and a
  // parent crumb that lands on a real page — the agent detail. We prefer
  // the ?agent=<id> link param, then the owning agent the GET response
  // carries (so links that pre-date the param still resolve the run
  // rather than dumping on the bare agents list); chat-mode memos were
  // produced inline in a chat.
  const parentAgentId = explicitAgentId || data?.run?.agentId || null;
  useSubCrumbs([
    mode === 'chat'
      ? { label: 'Chat', href: 'chat' }
      : { label: 'Agents', href: parentAgentId && runId ? `agents/${parentAgentId}/${runId}` : 'agents' },
    { label: data?.run?.title || 'Memo', href: null },
  ]);

  const action = async (payload) => {
    if (!runId) return;
    setBusy(true);
    try {
      const res = await memoPost(mode, runId, payload);
      if (res?.downloadUrl) setLastDownloadUrl(res.downloadUrl);
      await reload();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!runId) return <div className="page"><h1>Memo viewer</h1><p>Missing run id.</p></div>;
  if (error) return <div className="page"><h1>Memo viewer</h1><p style={{color:'#A20235'}}>{error}</p></div>;
  if (!data) return <div className="page"><h1>Memo viewer</h1><p>Loading…</p></div>;

  const sections = data.sections || [];

  const pendingSections = sections.filter(s =>
    s.status !== 'regenerating' &&
    (s.comments || []).some(c => !c.resolvedAt),
  );

  const regenerateAllPending = async () => {
    if (pendingSections.length === 0) return;
    setBulkProgress({ done: 0, total: pendingSections.length });
    setBusy(true);
    try {
      for (let i = 0; i < pendingSections.length; i++) {
        try {
          const res = await memoPost(mode, runId, {
            action: 'regenerate',
            sectionIndex: pendingSections[i].index,
            notes: '',
          });
          if (res?.downloadUrl) setLastDownloadUrl(res.downloadUrl);
        } catch (err) {
          setError(`Section ${pendingSections[i].index} regen failed: ${err.message || String(err)}`);
        }
        setBulkProgress({ done: i + 1, total: pendingSections.length });
      }
      await reload();
    } finally {
      setBusy(false);
      setBulkProgress(null);
    }
  };

  return (
    <div className="page">
      <div className="page__head">
        <h1>{data.run?.title || 'Memo'}</h1>
        <div className="lede">
          Comment section-by-section and regenerate just the sections that need it. Each regeneration re-builds the whole .docx so you always have an up-to-date file to download.
        </div>
        {pendingSections.length > 0 && (
          <div style={{
            marginTop:'var(--space-3)',
            padding:'10px 14px',
            background:'#FFE7DC',
            border:'1px solid #F08200',
            borderRadius:'var(--r-md)',
            display:'flex',alignItems:'center',gap:'var(--space-3)',flexWrap:'wrap',
          }}>
            <span style={{fontSize:13,color:'#B83C0F',fontWeight:500}}>
              {bulkProgress
                ? `Regenerating ${bulkProgress.done} of ${bulkProgress.total}…`
                : `${pendingSections.length} section${pendingSections.length === 1 ? '' : 's'} ${pendingSections.length === 1 ? 'has' : 'have'} open comments`}
            </span>
            <button
              type="button"
              className="btn-orange"
              onClick={regenerateAllPending}
              disabled={busy || bulkProgress !== null}
              style={{marginLeft:'auto'}}
            >
              {bulkProgress ? 'Working…' : `Regenerate all ${pendingSections.length}`}
            </button>
          </div>
        )}
        {lastDownloadUrl && (
          <div style={{marginTop:'var(--space-3)'}}>
            <a href={lastDownloadUrl} className="btn-orange">Download latest .docx</a>
          </div>
        )}
        {sections.length > 0 && (() => {
          const approvedCount = sections.filter(s => !!s.approvedAt).length;
          const allApproved = approvedCount === sections.length;
          return (
          <>
          <div style={{
            marginTop:'var(--space-3)',
            padding:'10px 14px',
            background: allApproved ? '#DCF1E2' : 'var(--bg-alt)',
            border: `1px solid ${allApproved ? '#00702A' : 'var(--border)'}`,
            borderRadius:'var(--r-md)',
            display:'flex',alignItems:'center',gap:'var(--space-3)',flexWrap:'wrap',
            fontSize:13,
          }}>
            <span style={{fontWeight:600, color: allApproved ? '#00702A' : 'var(--fg)'}}>
              {approvedCount} of {sections.length} section{sections.length === 1 ? '' : 's'} approved
            </span>
            {!allApproved && (
              <span style={{color:'var(--fg-muted)',fontSize:12}}>
                Approve every section to unlock export.
              </span>
            )}
            {allApproved && (
              <span style={{color:'#00702A',fontSize:12,fontWeight:500}}>
                Ready to ship — pick an export format below.
              </span>
            )}
          </div>
          <div style={{marginTop:'var(--space-3)',display:'flex',gap:'var(--space-2)',flexWrap:'wrap'}}>
            <a
              href={exportShellUrl({ src: previewUrl(mode, runId), kind: 'html', title: data.run?.title || 'Memo' })}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              title="Open a branded HTML rendering of the memo in a new tab. Re-renders from the canonical content store on every load — comments and regenerations flow through automatically."
            >Open HTML preview</a>
            {allApproved ? (
              <a
                href={exportShellUrl({ src: exportPdfUrl(mode, runId), kind: 'pdf', title: data.run?.title || 'Memo' })}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-orange"
                title="Export the approved memo as a branded PDF."
              >Export PDF</a>
            ) : (
              <button
                type="button"
                className="btn-ghost"
                disabled
                title={`Approve all ${sections.length} sections to enable export. ${approvedCount} done so far.`}
                style={{opacity:0.55,cursor:'not-allowed'}}
              >Export PDF (locked)</button>
            )}
            <button
              type="button"
              className="btn-ghost"
              disabled={shareBusy}
              title="Mint a public read-only URL anyone can open without an Eraneos login. The link can be revoked at any time."
              onClick={async () => {
                setShareError(null);
                setShareBusy(true);
                try {
                  const res = await fetch('/api/share-tokens', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                      target: mode === 'chat' ? 'chat-message' : 'agent-run',
                      id: runId,
                      kind: 'memo',
                    }),
                  });
                  const j = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(j.error || `share → ${res.status}`);
                  setShareUrl(new URL(j.url, publicOrigin()).toString());
                  setShareCopied(false);
                } catch (err) {
                  setShareError(err.message || String(err));
                } finally {
                  setShareBusy(false);
                }
              }}
            >{shareBusy ? 'Generating…' : 'Share read-only link'}</button>
          </div>
          </>
          );
        })()}
        {shareError && (
          <div style={{marginTop:'var(--space-2)',padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5}}>
            Couldn't create share link: {shareError}
          </div>
        )}
        {shareUrl && (
          <div
            style={{
              marginTop:'var(--space-3)',padding:'10px 14px',
              background:'var(--bg-alt)',
              border:'1px solid var(--border)',borderRadius:'var(--r-md)',
              display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap',
            }}
            role="status"
          >
            <span style={{fontSize:12,color:'var(--fg-muted)',whiteSpace:'nowrap'}}>Read-only share URL:</span>
            <code style={{flex:'1 1 240px',fontSize:12,padding:'4px 8px',background:'var(--bg)',borderRadius:4,wordBreak:'break-all'}}>
              {shareUrl}
            </code>
            <button
              type="button"
              className="btn-orange"
              onClick={async () => {
                try { await navigator.clipboard.writeText(shareUrl); setShareCopied(true); }
                catch { /* clipboard blocked — user can still select the code block */ }
              }}
              style={{padding:'5px 12px',fontSize:12}}
            >{shareCopied ? 'Copied ✓' : 'Copy'}</button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setShareUrl(null); setShareCopied(false); }}
              style={{padding:'5px 10px',fontSize:12}}
            >Dismiss</button>
          </div>
        )}
      </div>

      {sections.length === 0 && (
        <div className="kpi" style={{textAlign:'center'}}>
          <div className="kpi__label">No sections persisted yet</div>
          <p style={{marginTop:'var(--space-2)',color:'var(--fg-muted)'}}>
            This run hasn't produced a memo. Wait for the run to finish, or check that it ran with docx-render after migration 0058 was applied.
          </p>
        </div>
      )}

      {sections.map(section => (
        <SectionCard
          key={section.index}
          section={section}
          busy={busy}
          onComment={(sectionIndex, body)    => action({ action:'comment',    sectionIndex, body })}
          onRegenerate={(sectionIndex, notes) => action({ action:'regenerate', sectionIndex, notes })}
          onApprove={(sectionIndex)          => action({ action:'approve',    sectionIndex })}
          onUnapprove={(sectionIndex)        => action({ action:'unapprove',  sectionIndex })}
        />
      ))}
    </div>
  );
}
